/**
 * Reine Rechen- und Nummerierungslogik für Angebote/Rechnungen (framework-frei).
 * Die Persistenz liegt in der API; hier nur deterministische Funktionen.
 */

export type QuoteItemKind = 'group' | 'position' | 'text' | 'discount'
export type QuoteItemMode = 'mandatory' | 'optional' | 'alternative'

export interface CalcItem {
  id: string
  parentId?: string | null
  sortIndex: number
  kind: QuoteItemKind
  quantity: number
  unitPriceCents: number
  discountPercent: number
  mode: QuoteItemMode
  isChosenAlternative: boolean
  /** nur kind=discount: Bereichsgrenzen als numberLabel („1", „5.4"); null ⇒ ab Anfang / bis hier */
  rangeFromLabel?: string | null
  rangeToLabel?: string | null
}

export interface QuoteTotalsOptions {
  taxScheme: 'regel' | 'kleinunternehmer'
  taxRatePercent: number
  reverseChargeBau: boolean
}

export interface QuoteTotals {
  netCents: number
  taxCents: number
  grossCents: number
  optionalNetCents: number
  /** Nettosumme je Gruppen-Id (nur mitzählende Positionen) */
  groupSubtotals: Record<string, number>
  /** hierarchische Nummern je Item-Id */
  labels: Record<string, string>
  /** je discount-Zeile: berechnete Bemessungsgrundlage und (negativer) Betrag */
  discountLines: Record<string, { baseNetCents: number; amountCents: number }>
}

/** Netto einer einzelnen Position (Menge × EP × (1 − Rabatt%)). */
export function lineNetCents(item: Pick<CalcItem, 'quantity' | 'unitPriceCents' | 'discountPercent'>): number {
  const gross = item.quantity * item.unitPriceCents
  const afterDiscount = gross * (1 - (item.discountPercent || 0) / 100)
  return Math.round(afterDiscount)
}

/** Zählt diese Position in den Gesamtbetrag? */
function countsInMain(item: CalcItem): boolean {
  if (item.kind !== 'position') return false
  if (item.mode === 'mandatory') return true
  if (item.mode === 'alternative') return item.isChosenAlternative
  return false // optional
}

function sortTree(items: CalcItem[], parentId: string | null): CalcItem[] {
  return items
    .filter((i) => (i.parentId ?? null) === parentId)
    .sort((a, b) => a.sortIndex - b.sortIndex)
}

/** Tiefensuche in Dokumentreihenfolge (Eltern vor Kindern, sortIndex je Ebene). */
function flattenInOrder(items: CalcItem[]): CalcItem[] {
  const out: CalcItem[] = []
  const walk = (parentId: string | null) => {
    for (const item of sortTree(items, parentId)) {
      out.push(item)
      if (item.kind === 'group') walk(item.id)
    }
  }
  walk(null)
  return out
}

/**
 * Hierarchische Nummern („1", „1.1", „1.2", „2" …).
 * Gruppen, Positionen und Rabatt-Zeilen werden nummeriert, reine Textzeilen nicht.
 */
export function assignNumberLabels(items: CalcItem[]): Record<string, string> {
  const out: Record<string, string> = {}
  const walk = (parentId: string | null, prefix: string) => {
    let n = 0
    for (const item of sortTree(items, parentId)) {
      if (item.kind === 'text') {
        out[item.id] = ''
        continue
      }
      n += 1
      const label = prefix ? `${prefix}.${n}` : `${n}`
      out[item.id] = label
      if (item.kind === 'group') walk(item.id, label)
    }
  }
  walk(null, '')
  return out
}

/** „5.4" → [5, 4] */
function parseLabel(label: string): number[] {
  return label
    .split('.')
    .map((p) => Number.parseInt(p, 10))
    .filter((n) => Number.isFinite(n))
}

/** Tupel-Vergleich; kürzeres Tupel gilt als kleiner (Präfix). */
function cmpLabel(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length)
  for (let i = 0; i < len; i++) {
    if (a[i]! !== b[i]!) return a[i]! < b[i]! ? -1 : 1
  }
  return a.length === b.length ? 0 : a.length < b.length ? -1 : 1
}

/**
 * Fällt `label` in den Bereich [from, to]? `from`/`to` leer ⇒ offene Grenze.
 * Nachkommen der oberen Grenze („5.4.1" bei to=„5.4") zählen dazu.
 */
export function labelInRange(label: string, from: string | null | undefined, to: string | null | undefined): boolean {
  const L = parseLabel(label)
  if (from) {
    if (cmpLabel(L, parseLabel(from)) < 0) return false
  }
  if (to) {
    const T = parseLabel(to)
    const upper = L.slice(0, T.length) // auf Grenzentiefe kürzen → Nachkommen einschließen
    if (cmpLabel(upper, T) > 0) return false
  }
  return true
}

export function computeQuoteTotals(
  items: CalcItem[],
  opts: QuoteTotalsOptions,
  precomputedLabels?: Record<string, string>,
): QuoteTotals {
  const labels = precomputedLabels ?? assignNumberLabels(items)
  const groupSubtotals: Record<string, number> = {}
  const discountLines: Record<string, { baseNetCents: number; amountCents: number }> = {}

  // Beiträge in Dokumentreihenfolge: Positionen und (bereits gerechnete) Rabatte
  const contributions: { label: string; net: number }[] = []
  let netCents = 0
  let optionalNetCents = 0

  for (const item of flattenInOrder(items)) {
    if (item.kind === 'position') {
      const net = lineNetCents(item)
      if (countsInMain(item)) {
        netCents += net
        contributions.push({ label: labels[item.id] ?? '', net })
        // Zwischensummen der übergeordneten Gruppen
        let pid = item.parentId ?? null
        const guard = new Set<string>()
        while (pid && !guard.has(pid)) {
          guard.add(pid)
          groupSubtotals[pid] = (groupSubtotals[pid] ?? 0) + net
          pid = items.find((x) => x.id === pid)?.parentId ?? null
        }
      } else if (item.mode === 'optional') {
        optionalNetCents += net
      }
    } else if (item.kind === 'discount') {
      const base = contributions
        .filter((c) => labelInRange(c.label, item.rangeFromLabel, item.rangeToLabel))
        .reduce((s, c) => s + c.net, 0)
      const amount = -Math.round((base * (item.discountPercent || 0)) / 100)
      netCents += amount
      contributions.push({ label: labels[item.id] ?? '', net: amount })
      discountLines[item.id] = { baseNetCents: base, amountCents: amount }
    }
  }

  const taxFree = opts.taxScheme === 'kleinunternehmer' || opts.reverseChargeBau
  const taxCents = taxFree ? 0 : Math.round((netCents * opts.taxRatePercent) / 100)

  return {
    netCents,
    taxCents,
    grossCents: netCents + taxCents,
    optionalNetCents,
    groupSubtotals,
    labels,
    discountLines,
  }
}
